/** Shared table columns and form defaults for header/line-item mapping drawers */

export const MAPPING_TYPE = {
  HEADER: 'header',
  LINE_ITEM: 'lineItem',
};

export function defaultMappingFormData(headerCode, type) {
  return {
    headerCode: headerCode || '',
    ...(type === MAPPING_TYPE.LINE_ITEM ? { subCategory: '' } : {}),
    preSourceFieldName: '',
    preSourceJsonNode: '',
    preSourceFieldType: '',
    preSourceDefaultData: '',
    preSourceStaticData: '',
    preTransformScript: '',
    sourceFieldName: '',
    sourceFieldType: '',
    sourceDefaultData: '',
    sourceStaticData: '',
    postTransformScript: '',
    sequence: 0,
    active: true,
    targetColumn: '',
    targetWidth: '',
    targetDataType: '',
    apiId: '',
    nonAllowedCharacters: '',
    isSourceSystemId: false,
    isPreSourceDate: false,
    storeForParentIteration: false,
    mappingTable: '',
    mappingSourceColumn: '',
    mappingTargetColumn: '',
    mappingTargetApp: '',
  };
}

export function prepareMappingFormRow(row) {
  if (!row) return {};
  return {
    ...row,
    sequence: row.sequence ?? 0,
    active: row.active !== false,
    targetWidth: row.targetWidth ?? '',
    apiId: row.apiId || '',
    isSourceSystemId: !!row.isSourceSystemId,
    isPreSourceDate: !!row.isPreSourceDate,
    storeForParentIteration: !!row.storeForParentIteration,
  };
}

/** Fields tenant may edit on update (synced from global otherwise). */
const TENANT_HEADER_EDITABLE = new Set([
  'preTransformScript',
  'postTransformScript',
  'preSourceDefaultData',
  'preSourceStaticData',
  'sourceDefaultData',
  'sourceStaticData',
  'active',
  'mappingTable',
  'mappingSourceColumn',
  'mappingTargetColumn',
  'mappingTargetApp',
]);

const TENANT_LINE_ITEM_EDITABLE = new Set([
  'preTransformScript',
  'postTransformScript',
  'preSourceDefaultData',
  'preSourceStaticData',
  'sourceDefaultData',
  'sourceStaticData',
  'active',
]);

export function isMappingFieldReadOnly({ isTenant, editMode, type, fieldName }) {
  if (!isTenant || !editMode) return false;
  const allowed =
    type === MAPPING_TYPE.HEADER ? TENANT_HEADER_EDITABLE : TENANT_LINE_ITEM_EDITABLE;
  return !allowed.has(fieldName);
}

export function getMappingTableColumns(type) {
  const base = [
    { key: 'sequence', label: 'Seq', width: 56 },
    { key: 'targetColumn', label: 'Target Column', width: 140 },
    { key: 'preSourceFieldName', label: 'Pre Source Field', width: 140 },
    { key: 'sourceFieldName', label: 'Source Field', width: 140 },
    { key: 'active', label: 'Active', width: 64, format: (v) => (v === false ? 'No' : 'Yes') },
  ];
  if (type === MAPPING_TYPE.LINE_ITEM) {
    return [
      { key: 'sequence', label: 'Seq', width: 56 },
      { key: 'subCategory', label: 'Sub Category', width: 110 },
      ...base.slice(1),
    ];
  }
  return base;
}

export function formatMappingPayload(formData, type) {
  const trim = (v) => (v == null ? '' : String(v).trim());
  const payload = { ...formData };

  const intOrNull = (v) => {
    if (v === '' || v == null) return null;
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? null : n;
  };

  payload.sequence = intOrNull(formData.sequence) ?? 0;
  payload.targetWidth = intOrNull(formData.targetWidth);
  payload.active = formData.active !== false;
  payload.isSourceSystemId = !!formData.isSourceSystemId;
  payload.isPreSourceDate = !!formData.isPreSourceDate;
  if (type === MAPPING_TYPE.HEADER) {
    payload.storeForParentIteration = !!formData.storeForParentIteration;
  } else {
    delete payload.storeForParentIteration;
  }

  const uuidOrNull = trim(formData.apiId);
  payload.apiId = uuidOrNull || null;

  [
    'targetColumn',
    'targetDataType',
    'nonAllowedCharacters',
    'mappingTable',
    'mappingSourceColumn',
    'mappingTargetColumn',
    'mappingTargetApp',
  ].forEach((k) => {
    const t = trim(formData[k]);
    payload[k] = t || null;
  });

  return payload;
}
